import { useContext, useEffect, useMemo, useState } from 'react'
import firebase from '../../firebase/compat'

import Location from '../../hooks/Location'
import { useFirestoreGeneral, useFirestoreQuery } from '../../firebase/useFirestore'
import notDeleted from '../../helpers/notDeleted'
import { db } from '../../firebase/config'
import { Auth } from '../../StateManagment/Auth'
import { SavedIcon } from '../../StateManagment/SavedIcon'

import PageHeader from '../../components/layout/PageHeader'
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard'
import SelectableGrid from '../../components/common/SelectableGrid'
import CitySelect from './scope/CitySelect'
import WijkSelect from './scope/WijkSelect'
import AddressSelect from './scope/AddressSelect'
import Registratie from './Registratie'
import Rollup from './Rollup'
import { defaultCampaignView } from '../../helpers/wervingsacties/defaultCampaignView'
import { planSelect, planDeselect } from '../../helpers/wervingsacties/selectionPlan'
import { writeDeselection, writeSelection } from '../../helpers/wervingsacties/writeSelection'
import useBuurtNames from '../../hooks/wervingsacties/useBuurtNames'
import useWijkAdressen from '../../hooks/wervingsacties/useWijkAdressen'
import useSettings from '../../hooks/Settings.jsx'
import resolveWervingsDiensten, { dienstNamen as toDienstNamen } from '../../helpers/wervingsacties/resolveWervingsDiensten'

const VIEW_OPTIONS = [
    { id: 'overzicht', label: 'Overzicht' },
    { id: 'scope', label: 'Gebied' },
    { id: 'registratie', label: 'Registratie' },
]

const Detail = () => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // null tot de actie binnen is; daarna kiest de gebruiker.
    const [view, setView] = useState(/** @type {string | null} */ (null))

    const id = Location()[3]

    const acties = useFirestoreGeneral('Wervingsacties', 'ID', id)
    const campaign = (Array.isArray(acties) ? acties : []).find((a) => !a.Deleted) || null

    // Óók de zacht verwijderde rijen: wie een adres opnieuw aanvinkt, krijgt
    // zijn oude rij terug (met wat er al was ingevuld) in plaats van een tweede.
    //
    // `initial: null` en een onError: zolang de rijen laden of als de read
    // mislukt, is 'niets gekozen' onwaar. Een klik in dat gat maakte dubbele
    // rijen naast de bestaande, want het plan zag een lege lijst. Daarom tonen
    // stap 3, 'Registratie' en 'Overzicht' pas iets als de rijen er zijn.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const [rijenFout, setRijenFout] = useState(/** @type {{ code?: string, message?: string } | null} */ (null))
    const registratiesAll = useFirestoreQuery('Wervingsregistraties', {
        where: [['WervingsactieID', '==', id]],
        includeDeleted: true,
        initial: null,
        onError: setRijenFout,
    })
    const rijenGeladen = Array.isArray(registratiesAll)
    // Onthouden per snapshot: een nieuwe array per render liet de groepering
    // van 3.550 adressen in AddressSelect bij elke kleine wijziging opnieuw lopen.
    const alleRijen = useMemo(() => (Array.isArray(registratiesAll) ? registratiesAll : []), [registratiesAll])
    const registraties = useMemo(() => alleRijen.filter(notDeleted), [alleRijen])

    const rijenMelding = rijenFout ? (
        <p className='wa-error' role='alert'>
            {`De gekozen adressen konden niet geladen worden (${rijenFout.code || rijenFout.message || 'onbekende fout'}). `}
            Kiezen en registreren staat uit. Herlaad de pagina om het opnieuw te proberen.
        </p>
    ) : !rijenGeladen ? (
        <p className='wa-hint'>Gekozen adressen laden</p>
    ) : null

    // Eén keer kiezen, als de actie binnenkomt. Meelopen met de gegevens zou
    // het scherm naar 'Overzicht' laten springen zodra je je eerste wijk kiest.
    useEffect(() => {
        if (campaign && view === null) setView(defaultCampaignView(campaign))
    }, [campaign, view])

    const gemeenten = campaign?.Gemeenten || []
    const wijken = campaign?.Wijken || []
    const { names: buurtNames, loadState: buurtNamenStand } = useBuurtNames(wijken)
    // Hier en niet in AddressSelect: die staat alleen op 'Gebied', en elk
    // bezoek aan dat tabblad haalde de hele wijk opnieuw op bij PDOK.
    const wijkAdressen = useWijkAdressen(wijken)

    // De diensten van de organisatie, voor het registratievenster en de kolom
    // uitkomst. Eén keer hier, niet per scherm: elke useSettings() is een eigen listener.
    const { wervingsDienstenConfig, loaded: instellingenGeladen } = useSettings()
    // Zolang de instellingen laden, kent het scherm de diensten niet: 'Registratie'
    // en 'Overzicht' zouden 'Onbekende dienst' tonen, ook in de download.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const instellingenMelding = instellingenGeladen ? null : <p className='wa-hint'>Instellingen laden</p>
    const diensten = useMemo(() => resolveWervingsDiensten({ WervingsDiensten: wervingsDienstenConfig }), [wervingsDienstenConfig])
    const dienstNamen = useMemo(() => toDienstNamen({ WervingsDiensten: wervingsDienstenConfig }), [wervingsDienstenConfig])

    const updateScope = async (patch) => {
        if (!campaign) return
        await db.collection('Wervingsacties').doc(campaign.docid).update(patch)
        setSaved('flex')
    }

    const addGemeente = (g) => {
        if (gemeenten.some((x) => x.GemeenteCode === g.GemeenteCode)) return
        updateScope({ Gemeenten: [...gemeenten, g] })
    }

    const removeGemeente = (code) => {
        // Dropping a city also drops its wijken from scope. Existing registrations
        // for those wijken are intentionally kept (preserved fieldwork), not deleted.
        updateScope({
            Gemeenten: gemeenten.filter((x) => x.GemeenteCode !== code),
            Wijken: wijken.filter((w) => w.GemeenteCode !== code),
        })
    }

    const toggleWijk = (w) => {
        const exists = wijken.some((x) => x.WijkCode === w.WijkCode)
        updateScope({ Wijken: exists ? wijken.filter((x) => x.WijkCode !== w.WijkCode) : [...wijken, w] })
    }

    // Eén adres of een hele groep: het plan bepaalt wat er aangemaakt,
    // teruggezet of losgelaten wordt (ADR-008: loslaten is zacht verwijderen).
    // Een mislukte opslag gooit door, zodat hij niet stil verdwijnt.
    // Na de hoogste bestaande positie, ook van losgelaten rijen: het aantal
    // levende rijen + 1 botste zodra er rijen losgelaten waren.
    const nextPosition = () => alleRijen.reduce((max, r) => Math.max(max, Number(r.Position) || 0), 0) + 1

    /**
     * @param {{ written: number, total: number, error: Error | null }} result
     * @returns {{ written: number, total: number, error: Error | null }}
     */
    const afronden = (result) => {
        if (result.written > 0) setSaved('flex')
        if (result.error) throw result.error
        return result
    }

    const selectAddresses = async (addresses, onProgress) => afronden(await writeSelection({
        plan: planSelect(addresses, alleRijen),
        wervingsactieId: id,
        firstPosition: nextPosition(),
        onProgress,
    }))

    // Opmerking per straat op het actiedocument. Met een FieldPath: een
    // straatnaam kan een punt bevatten ('St. Janstraat'), en een dotted path
    // zou daarop splitsen. Leeg = de opmerking weghalen.
    // AI-CONTEXT: See src/helpers/wervingsacties/straatKey.js
    const saveOpmerking = async (key, tekst) => {
        const schoon = (tekst || '').trim()
        await db.collection('Wervingsacties').doc(campaign.docid).update(
            new firebase.firestore.FieldPath('StraatOpmerkingen', key),
            schoon
                ? { Tekst: schoon, Door: auth?.ID || '', Op: firebase.firestore.FieldValue.serverTimestamp() }
                : firebase.firestore.FieldValue.delete(),
        )
        setSaved('flex')
    }

    const deselectAddresses = async (addresses, onProgress) => afronden(await writeDeselection({
        plan: planDeselect(addresses, alleRijen),
        authId: auth?.ID,
        onProgress,
    }))

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading={campaign?.Title || 'Wervingsactie'}
                    description={'Stel het gebied samen: kies steden, wijken en adressen'}
                    AI={false}
                />

                {campaign && view && (
                    <>
                        <SelectableGrid
                            items={VIEW_OPTIONS}
                            selectedId={view}
                            getId={(item) => item.id}
                            getLabel={(item) => item.label}
                            onClick={setView}
                        />
                        <ReadOnlyGuard edit='housevisits.edit'>
                            {(canEdit) => (
                                view === 'scope' ? (
                                    <div className='wervingsacties-scope'>
                                        <CitySelect
                                            selected={gemeenten}
                                            onAdd={addGemeente}
                                            onRemove={removeGemeente}
                                            canEdit={canEdit}
                                        />
                                        <WijkSelect
                                            gemeenten={gemeenten}
                                            selectedWijken={wijken}
                                            onToggle={toggleWijk}
                                            canEdit={canEdit}
                                        />
                                        {wijken.length > 0 && (rijenMelding || (
                                            <AddressSelect
                                                wijken={wijken}
                                                registraties={registraties}
                                                adressen={wijkAdressen.options}
                                                adressenStand={wijkAdressen.loadState}
                                                buurtNames={buurtNames}
                                                straatOpmerkingen={campaign.StraatOpmerkingen || {}}
                                                onSaveOpmerking={saveOpmerking}
                                                onSelect={selectAddresses}
                                                onDeselect={deselectAddresses}
                                                canEdit={canEdit}
                                            />
                                        ))}
                                    </div>
                                ) : view === 'registratie' ? (
                                    rijenMelding || instellingenMelding || <Registratie registraties={registraties} buurtNames={buurtNames} diensten={diensten} dienstNamen={dienstNamen} canEdit={canEdit} />
                                ) : (
                                    rijenMelding || instellingenMelding || (
                                        <Rollup
                                            registraties={registraties}
                                            wijken={wijken}
                                            buurtNames={buurtNames}
                                            adressen={wijkAdressen.options}
                                            adressenStand={wijkAdressen.loadState}
                                            diensten={diensten}
                                            dienstNamen={dienstNamen}
                                            straatOpmerkingen={campaign.StraatOpmerkingen || {}}
                                            titel={campaign.Title || ''}
                                            buurtNamenStand={buurtNamenStand}
                                        />
                                    )
                                )
                            )}
                        </ReadOnlyGuard>
                    </>
                )}
            </div>
        </div>
    )
}

export default Detail
